/**
 * IRP Workbook Main Component
 *
 * Shell layout:
 * - A left vertical icon rail for tab navigation, full height.
 * - A slim top bar in the content column: mapping-health summary, a Filters
 *   dropdown, the Safety View chip, and the fullscreen toggle.
 * - The active tab's content fills the rest.
 */

import React, {useState} from 'react';
import {Box, Chip, Portal, Tooltip} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {useActiveTab, useOverviewCache} from '../../../../../stores/irp-workbook.store';
import {useIRPContext} from './IRPWorkbookProvider';
import DataSummary from './components/DataSummary';
import MappingHealthSummary from './components/MappingHealthSummary';
import FilterButton from './components/FilterButton';
import OverviewBarControls from './components/OverviewBarControls';
import PrecursorBarControls from './components/PrecursorBarControls';
import TabNavigationBar from './components/TabNavigationBar';
import OverviewTab from './components/tabs/OverviewTab';
import SkippedReportsTab from './components/tabs/SkippedReportsTab';
import PrecursorBarrierTab from './components/tabs/PrecursorBarrierTab';
import ReportAnalysisTab from './components/tabs/ReportAnalysisTab';
import BarrierEffectivenessTab from './components/tabs/BarrierEffectivenessTab';
import OccurrencePatternsTab from './components/tabs/OccurrencePatternsTab';
import NtsbRecommendationsTab from './components/tabs/NtsbRecommendationsTab';
import {
    describeMappingStatus,
    describeMappingStatusDetail,
} from '../component-mapping/mapping-status-text';

const IRPWorkbook: React.FC = () => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const activeTab = useActiveTab();
    const overviewCache = useOverviewCache();
    const {componentMapping} = useIRPContext();

    // Toggle fullscreen handler
    const toggleFullscreen = (): void => {
        setIsFullscreen(!isFullscreen);
    };

    // Workbook content (shared between normal and fullscreen)
    const workbookContent = (
        <Box sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            backgroundColor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Tab rail (left, full height) + content column (right) */}
            <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden', display: 'flex' }}>
                <TabNavigationBar />

                <Box sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Slim top bar: mapping-health + Filters + Safety View + fullscreen */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                        flexShrink: 0
                    }}>
                        <MappingHealthSummary overview={overviewCache} compact={activeTab !== 'overview'} />
                        {componentMapping?.mappingStatus && (
                            <Tooltip title={describeMappingStatusDetail(componentMapping.mappingStatus)}>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={describeMappingStatus(componentMapping.mappingStatus)}
                                    sx={{ height: 24, fontSize: '0.7rem' }}
                                />
                            </Tooltip>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        {activeTab === 'overview' && <OverviewBarControls />}
                        {activeTab === 'precursor-barrier' && <PrecursorBarControls />}
                        {/* Component mapping endpoints do not support server-side filters yet */}
                        {!componentMapping && <FilterButton />}
                        <DataSummary />
                        <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
                            <Chip
                                icon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                label={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                                size="small"
                                color="primary"
                                variant="outlined"
                                onClick={toggleFullscreen}
                                sx={{
                                    height: 24,
                                    fontSize: '0.7rem',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: 'primary.main',
                                        color: 'primary.contrastText',
                                        '& .MuiChip-icon': {
                                            color: 'primary.contrastText'
                                        }
                                    }
                                }}
                            />
                        </Tooltip>
                    </Box>

                    {/* Tab content fills the rest */}
                    <Box sx={{
                        flexGrow: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        p: 2
                    }}>
                        {activeTab === 'overview' && <OverviewTab />}
                        {activeTab === 'skipped-reports' && !componentMapping && <SkippedReportsTab />}
                        {activeTab === 'precursor-barrier' && <PrecursorBarrierTab />}
                        {activeTab === 'report-analysis' && <ReportAnalysisTab />}
                        {activeTab === 'barrier-effectiveness' && <BarrierEffectivenessTab />}
                        {activeTab === 'occurrence-patterns' && <OccurrencePatternsTab />}
                        {activeTab === 'ntsb-recommendations' && <NtsbRecommendationsTab />}
                    </Box>
                </Box>
            </Box>
        </Box>
    );

    // Main render with conditional fullscreen
    if (isFullscreen) {
        return (
            <Portal>
                <Box sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 1300,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {workbookContent}
                </Box>
            </Portal>
        );
    }

    return workbookContent;
};

export default IRPWorkbook;